import { useUser, useAuth } from "@clerk/clerk-react";
import { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { BACKEND_URL } from "../constants/api";
import { Button } from "@/components/ui/button";
import {
  School,
  BarChart2,
  Zap,
  ArrowRight,
  CheckCircle2,
  X,
  Clock,
  BookOpen,
  Plus,
} from "lucide-react";
import useSubscriptionInfo from "@/hooks/useSubscriptionInfo";

function getGreeting() {
  const hour = new Date().getHours();
  if (hour < 12) return "Good morning";
  if (hour < 17) return "Good afternoon";
  return "Good evening";
}

function timeAgo(dateStr) {
  const diff = Date.now() - new Date(dateStr).getTime();
  const mins = Math.floor(diff / 60000);
  if (mins < 1) return "just now";
  if (mins < 60) return `${mins}m ago`;
  const hrs = Math.floor(mins / 60);
  if (hrs < 24) return `${hrs}h ago`;
  const days = Math.floor(hrs / 24);
  if (days < 7) return `${days}d ago`;
  const weeks = Math.floor(days / 7);
  if (weeks < 5) return `${weeks}w ago`;
  const months = Math.floor(days / 30);
  return `${months}mo ago`;
}

export default function Dashboard() {
  const { user } = useUser();
  const { getToken } = useAuth();
  const navigate = useNavigate();
  const [paymentSuccess, setPaymentSuccess] = useState(false);
  const [initError, setInitError] = useState(false);
  const [recentClasses, setRecentClasses] = useState([]);
  const [classesLoading, setClassesLoading] = useState(true);
  const { planName, loading: subLoading } = useSubscriptionInfo();

  useEffect(() => {
    const init = async () => {
      try {
        const token = await getToken();

        const [initRes, classRes] = await Promise.all([
          fetch(`${BACKEND_URL}api/user/create_if_new`, {
            method: "POST",
            headers: {
              "Content-Type": "application/json",
              Authorization: `Bearer ${token}`,
            },
            body: JSON.stringify({
              clerkId: user.id,
              email: user.primaryEmailAddress.emailAddress,
              fullName: user.fullName,
            }),
          }),
          fetch(`${BACKEND_URL}api/report-groups`, {
            headers: { Authorization: `Bearer ${token}` },
          }),
        ]);

        if (!initRes.ok) throw new Error("Failed to initialize account");

        if (classRes.ok) {
          const all = await classRes.json();
          const sorted = [...all]
            .sort((a, b) => new Date(b.updatedAt) - new Date(a.updatedAt))
            .slice(0, 4);
          setRecentClasses(sorted);
        }
      } catch {
        setInitError(true);
      } finally {
        setClassesLoading(false);
      }
    };

    if (user) init();

    const query = new URLSearchParams(window.location.search);
    if (query.get("success")) {
      setPaymentSuccess(true);
      navigate("/dashboard", { replace: true });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user]);

  const firstName =
    user?.firstName || user?.fullName?.split(" ")[0] || "Teacher";
  const showUpgradeCta = !subLoading && planName === "free";

  return (
    <div className="dashboard-content">
      {paymentSuccess && (
        <div className="flex items-center justify-between gap-3 rounded-lg border border-green-200 bg-green-50 px-4 py-3 text-green-800 text-sm mb-4">
          <div className="flex items-center gap-2">
            <CheckCircle2 size={16} className="shrink-0" />
            <span>Payment successful! Your plan has been upgraded.</span>
          </div>
          <button
            onClick={() => setPaymentSuccess(false)}
            className="shrink-0 text-green-600 hover:text-green-800"
          >
            <X size={15} />
          </button>
        </div>
      )}
      {initError && (
        <div className="flex items-center justify-between gap-3 rounded-lg border border-red-200 bg-red-50 px-4 py-3 text-red-800 text-sm mb-4">
          <div className="flex items-center gap-2">
            <X size={16} className="shrink-0" />
            <span>Could not connect to the server. Please refresh the page.</span>
          </div>
          <button
            onClick={() => setInitError(false)}
            className="shrink-0 text-red-600 hover:text-red-800"
          >
            <X size={15} />
          </button>
        </div>
      )}

      {/* Welcome header */}
      <div className="dashboard-welcome">
        <div>
          <h1 className="dashboard-heading">
            {getGreeting()}, {firstName}.
          </h1>
          <p className="dashboard-subheading">Pick up where you left off.</p>
        </div>
        {showUpgradeCta && (
          <Link to="/billing">
            <Button size="sm" variant="outline" className="upgrade-cta">
              <Zap size={14} />
              Upgrade Plan
            </Button>
          </Link>
        )}
      </div>

      {/* Recent Classes */}
      <div className="recent-section">
        <div className="recent-section-header">
          <h2 className="quick-actions-heading">Recent Classes</h2>
          <Link to="/classes" className="recent-view-all">
            View all <ArrowRight size={12} />
          </Link>
        </div>

        {classesLoading ? (
          <div className="recent-classes-grid">
            {[0, 1, 2, 3].map((i) => (
              <div key={i} className="recent-class-skeleton" />
            ))}
          </div>
        ) : recentClasses.length === 0 ? (
          <div className="recent-empty">
            <BookOpen size={26} className="recent-empty-icon" />
            <p className="recent-empty-title">No classes yet</p>
            <p className="recent-empty-sub">
              Create your first class to start generating report cards.
            </p>
            <Link to="/classes">
              <Button size="sm" variant="outline" className="gap-2 mt-3">
                <Plus size={13} />
                New Class
              </Button>
            </Link>
          </div>
        ) : (
          <div className="recent-classes-grid">
            {recentClasses.map((cls) => (
              <Link
                key={cls._id}
                to={`/classes/${cls._id}`}
                className="recent-class-card"
              >
                <div className="recent-class-icon">
                  <School size={17} />
                </div>
                <div className="recent-class-info">
                  <div className="recent-class-name">{cls.name}</div>
                  {(cls.grade || cls.schoolYear) && (
                    <div className="recent-class-meta">
                      {[
                        cls.grade && `Grade ${cls.grade}`,
                        cls.schoolYear,
                      ]
                        .filter(Boolean)
                        .join(" · ")}
                    </div>
                  )}
                </div>
                <div className="recent-class-right">
                  <span className="recent-class-time">
                    <Clock size={10} />
                    {timeAgo(cls.updatedAt)}
                  </span>
                  <ArrowRight size={13} className="recent-class-arrow" />
                </div>
              </Link>
            ))}
          </div>
        )}
      </div>

      {/* Quick Actions */}
      <div className="quick-actions">
        <h2 className="quick-actions-heading">Quick Actions</h2>
        <div className="quick-actions-grid">
          <Link to="/classes" className="quick-action-card">
            <div className="quick-action-icon">
              <School size={22} />
            </div>
            <div>
              <div className="quick-action-title">My Classes</div>
              <div className="quick-action-desc">
                View, create, and manage your report card classes
              </div>
            </div>
            <ArrowRight size={16} className="quick-action-arrow" />
          </Link>

          <Link to="/usage" className="quick-action-card">
            <div className="quick-action-icon">
              <BarChart2 size={22} />
            </div>
            <div>
              <div className="quick-action-title">View Usage</div>
              <div className="quick-action-desc">
                Track your report card and AI generation usage
              </div>
            </div>
            <ArrowRight size={16} className="quick-action-arrow" />
          </Link>
        </div>
      </div>
    </div>
  );
}
